import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import CancelActionButton from 'components/CancelActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox, { CheckboxSc } from 'components/final-form/CheckBox';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import { dropzoneInput } from 'components/final-form/DropZoneInput';
import Select, { PxDropdownSc } from 'components/final-form/Select';
import { selectInput } from 'components/final-form/SelectInput';
import { textInput } from 'components/final-form/TextInput';
import { GuarantorsConsumerRenderProps, withGuarantorsContext } from 'components/guarantors/GuarantorsContext';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { IndexedZipData } from 'components/patient/upsert/UpsertPatient';
import { sortZipData } from 'components/patient/utils/patientUtils';
import SaveActionButton from 'components/SaveActionButton';
import { FormApi } from 'final-form';
import createDecorator from 'final-form-calculate';
import _ from "lodash";
import React, { Component } from "react";
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, DropdownProps, Grid, Header, Icon, Table } from 'semantic-ui-react';
import { getAllArticleGroups } from 'services/articleGroupServices';
import { editCompany, getAllGuarantorCompanies, saveCompany } from 'services/companyServices';
import { getAllCountries } from 'services/countryServices';
import { fetchAllProducts } from 'services/productServices';
import { getContainers } from 'services/selfServiceContainerServices';
import { searchZipDataForInput } from 'services/zipDataServices';
import styled from 'styled-components';
import {
  CompanySubType,
  CompanyType,
  CovidTestSampleMethod,
  LabInvoiceTo,
  Privilege,
  RegistrationStateType,
  RepetitiveTestingMode,
  TestPoolingType,
  TestRegistrationType,
  ZipDataOrderBy
} from 'ts-types/api.enums';

import { ArticleGroupDto, CompanyArticleGroupDto, ProductDto, UpsertCompanyDto } from 'ts-types/api.types';
import { testExternalCodes } from 'utils/enumUtils';
import { errorUtils } from 'utils/errorUtils';
import { toBase64 } from 'utils/fileUtils';
import { noop } from 'utils/functionUtils';
import { IndexSignature } from 'utils/praxTypes';
import { required } from 'utils/validatorUtils';

const UpsertForm = styled.form`
  & .ui.grid > .row.sectionHeader {
    margin-top: 1rem;
  }
`;

const TableDiv = styled.div`

  .dropdown {
  }

  .dropdown.error-mandatory-field {
    border-color: #e0b4b4;
    background-color: #fff6f6;
  }
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps, GuarantorsConsumerRenderProps {
  toggleAddEditModal: () => void,
  onSave: () => void,
  onCancel: () => void,
  companyId?: number,
  company?: UpsertCompanyDto
}

interface State {
  errorMessage?: string,
  billTexts: DropdownOption[],
  companyTypes: DropdownOption[],
  companySubTypes: DropdownOption[],
  countries?: DropdownOption[],
  guarantorCompanies?: DropdownOption[],
  articleGroups: Map<number, ArticleGroupDto>,
  articleGroupsPerExternalCode: Map<string, DropdownOption[]>,
  cities: DropdownOption[],
  zip: DropdownOption[],
  articleGroupId?: number,
  articleGroupIx?: number,
  productId?: string,
  productDataSelected: boolean,
  sampleMethod?: string,
  logistics?: boolean,
  generateCertificate?: boolean,
  generateReport?: boolean,
  supplier: string,
  supplierIx?: number,
  testRegistrationTypes?: DropdownOption[],
  testTypes?: DropdownOption[],
  testSampleMethods: DropdownOption[],
  testPoolingTypes?: DropdownOption[],
  repetitiveTestingModes?: DropdownOption[],
  products: Map<string, ProductDto>,
  productsPerExternalCode: Map<string, DropdownOption[]>,
  labInvoiceTo: DropdownOption[],
  containerOptions: DropdownOption[],
  articleGroupDataSelected: boolean,
  sampleMethodDataSelected: boolean,
  requiredDataLoaded: boolean,
}

class UpsertCompanyForm extends Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  initialCompanyValues: Partial<UpsertCompanyDto> = {};

  constructor(props: Props) {
    super(props);

    const {company, getMessage} = this.props;

    const billTextLabels: string[] = ["german", "french", "italian", "english"];
    const billTextKeys: string[] = ["de", "fr", "it", "en"];

    const companyTypesLabels: string[] = Object.values(CompanyType);
    const companySubTypesLabels: string[] = Object.values(CompanySubType);
    const testSampleMethodsLabels: string[] = Object.values(CovidTestSampleMethod);

    const labInvoiceLabels: string[] = Object.values(LabInvoiceTo);

    const initialCities: Array<DropdownOption> = [];
    const initialZip: Array<DropdownOption> = [];

    if (company) {

      if (company.city) {
        initialCities.push({
          key: company.city,
          text: company.city,
          value: company.city,
          predefinedOption: true
        })
      }

      if (company.zip) {
        initialZip.push({
          key: company.zip,
          text: company.zip,
          value: company.zip,
          predefinedOption: true
        })
      }
    }

    const testRegistrationTypes: Array<DropdownOption> = Object.values(TestRegistrationType).map((value: string) => {
      return {
        key: value,
        text: getMessage(`testRegistrationType.${value.toUpperCase()}`),
        value: value
      }
    });

    const filteredExternalTestCodes = testExternalCodes();
    const testTypes: Array<DropdownOption> = filteredExternalTestCodes.map((value: string) => {
      return {
        key: value,
        text: getMessage(`testType.InterventionExternalCode.${value.toUpperCase()}`),
        value: value
      }
    });

    const testPoolingTypes: Array<DropdownOption> = Object.values(TestPoolingType).map((value: string) => {
      return {
        key: value,
        text: getMessage(`testPoolingType.${value.toUpperCase()}`),
        value: value
      }
    });

    const repetitiveTestingModes: Array<DropdownOption> =
        Object.values(RepetitiveTestingMode).map((value: string) => {
          return {
            key: value,
            text: getMessage(`repetitiveTestingMode.${value.toUpperCase()}`),
            value: value
          }
        });

    this.state = {
      billTexts: stringArrayToDropdownOptionArray(
          billTextLabels, getMessage, "billText", billTextKeys),

      companyTypes: stringArrayToDropdownOptionArray(
          companyTypesLabels, getMessage, "enum.companyType"),

      companySubTypes: stringArrayToDropdownOptionArray(
          companySubTypesLabels, getMessage, "enum.companySubType"),

      labInvoiceTo: stringArrayToDropdownOptionArray(
          labInvoiceLabels, getMessage, "company.enum.labInvoiceTo"),

      cities: initialCities,
      zip: initialZip,
      articleGroups: new Map<number, ArticleGroupDto>(),
      articleGroupsPerExternalCode: new Map(),
      supplier: "",
      testRegistrationTypes: testRegistrationTypes,
      testTypes: testTypes,
      testSampleMethods: stringArrayToDropdownOptionArray(
          testSampleMethodsLabels, getMessage, "covidTestSampleMethod"),
      testPoolingTypes: testPoolingTypes,
      repetitiveTestingModes,
      containerOptions: [],
      products: new Map(),
      productsPerExternalCode: new Map<string, DropdownOption[]>(),
      articleGroupDataSelected: false,
      productDataSelected: false,
      sampleMethodDataSelected: false,
      requiredDataLoaded: false
    };

    this.fetchRequiredData(props);
  };

  componentWillUnmount(): void {
    this.cancelTokenSource.cancel('Operation canceled by user');
  }

  fetchRequiredData = (props: Props) => {

    const {currentUser} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;

    Promise.all([
          getAllCountries(this.cancelTokenSource),
          getAllGuarantorCompanies(mandateId, this.cancelTokenSource),
          getAllArticleGroups(this.cancelTokenSource),
          getContainers(mandateId, this.cancelTokenSource),
          fetchAllProducts(true, this.cancelTokenSource)
        ])
        .then(responses => {

          const {company, currentUser} = this.props;

          const countries = mapToDropdownOptionArray(responses[0], "name", "shortName");
          const guarantorCompanies = mapToDropdownOptionArray(responses[1], "description", "id");
          const articleGroupList = responses[2];
          const productList: ProductDto[] = responses[4];

          const containerDtos = responses[3].filter(container =>
              container.active || (company && company.container === container.id));
          const containers = mapToDropdownOptionArray(containerDtos, "description", "id", "id");

          let articleGroupsMap = new Map<number, ArticleGroupDto>();
          articleGroupList.forEach(articleGroup => {
            articleGroupsMap.set(articleGroup.id, articleGroup);
          });


          const articleGroupsPerExternalCode: Map<string, DropdownOption[]> = new Map();

          const agPerExternalCode: { [key: string]: ArticleGroupDto[] } =
              _.groupBy(articleGroupList, ag => ag.externalCode);

          _.keys(agPerExternalCode).forEach(extCode => {
            const optionsForCode: DropdownOption[] = agPerExternalCode[extCode].map(ag => ({
              key: ag.id,
              value: ag.id,
              text: [ag.description, ag.externalOrderNumber].filter(Boolean).join(", ")
            }));
            articleGroupsPerExternalCode.set(extCode, optionsForCode);
          });

          // products
          let availableProducts = productList.filter(product => product.active && product.selfService);
          availableProducts = availableProducts.filter(product => {
            const items = product.productItems;
            const privateItems = items.filter(item => item.cashOptions).length;
            const insuranceItems = items.filter(item => !item.cashOptions).length;
            return privateItems > 0 && insuranceItems === 0;
          })

          let productMap = new Map<string, ProductDto>();
          availableProducts.forEach(product => {
            productMap.set(product.id, product);
          });

          const productsPerExternalCode: { [key: string]: ProductDto[] } =
              _.groupBy(availableProducts, ag => ag.externalCode);

          const productOptsPerExternalCode: Map<string, DropdownOption[]> = new Map();
          _.keys(productsPerExternalCode).forEach(extCode => {
            const optionsForCode: DropdownOption[] = productsPerExternalCode[extCode].map(product => ({
              key: product.id,
              value: product.id,
              text: [product.id, product.description].filter(Boolean).join(" - ")
            }));
            productOptsPerExternalCode.set(extCode, optionsForCode);
          });

          // update state
          this.setState({
            countries,
            guarantorCompanies,
            containerOptions: containers,
            articleGroups: articleGroupsMap,
            articleGroupsPerExternalCode: articleGroupsPerExternalCode,
            products: productMap,
            productsPerExternalCode: productOptsPerExternalCode
          });

          if (company) {
            this.initialCompanyValues = {...company};
          } else {
            this.initialCompanyValues = {
              active: true,
              labInvoiceTo: LabInvoiceTo.GUARANTOR,
              language: 'de',
              countryId: countries ? countries[0].value : undefined,
              mandateId: currentUser!.userConfig.mandateId
            };
          }

        })
        .catch(noop)
        .finally(() => {
          this.setState({
            requiredDataLoaded: true
          });
        })
  }

  mapZipData = (field: string, zipData: Array<IndexedZipData>, orderBy: ZipDataOrderBy): Array<DropdownOption> => {

    const sortedZipData = sortZipData(zipData, orderBy);

    return sortedZipData.map(data => ({
      key: data.id,
      text: data[field],
      value: `${data.zip}, ${data.city}, ${data.countryId}`,
      content: `${data.zip}, ${data.city}, ${data.canton}`,
      predefinedOption: true
    }))
  };

  searchZipData = (query: string, countryId: number) => {

    const {cities, zip: zips} = this.state;

    searchZipDataForInput(query, countryId, this.cancelTokenSource, 75)
        .then(response => {

          const allInitialAndNewCityAdditions = cities.filter(city => city.value == city.key);
          const allInitialAndNewZipAdditions = zips.filter(zip => zip.value == zip.key);

          this.setState({
            cities: [...allInitialAndNewCityAdditions, ...this.mapZipData("city", response, ZipDataOrderBy.CITY)],
            zip: [...allInitialAndNewZipAdditions, ...this.mapZipData("zip", response, ZipDataOrderBy.ZIP)]
          })
        })
        .catch(() => {
          this.setState({
            cities: [],
            zip: []
          })
        })
  };

  onNewAddition = (options: "zip" | "cities") => (newAddition: DropdownOption) => {

    const {cities, zip: zips} = this.state;

    if (options == "cities") {

      const foundCity = cities.find(city => city.key == newAddition.key);

      if (!foundCity) {
        newAddition.predefinedOption = false;
        const newCities = cities.filter(city => city.predefinedOption === true);
        this.setState({
          cities: [newAddition, ...newCities]
        });
      }
    } else if (options == "zip") {

      const foundZip = zips.find(zip => zip.key == newAddition.key);

      if (!foundZip) {
        newAddition.predefinedOption = false;
        const newZips = zips.filter(city => city.predefinedOption === true);
        this.setState({
          zip: [newAddition, ...newZips]
        });
      }
    }
  };

  updateZipDataFieldDecorator = (field: string) =>
      (zipData?: string, allValues?: Partial<UpsertCompanyDto & IndexSignature>) => {

        if (zipData) {
          const splitZipData = zipData.split(", ");

          if (splitZipData.length == 3 && allValues && allValues[field] != zipData) {
            return zipData;
          }
        }

        return allValues ? allValues[field] : undefined;
      };

  zipDataDecorator = createDecorator(
      {
        field: "city",
        updates: {
          zip: this.updateZipDataFieldDecorator("zip")
        }
      },
      {
        field: "zip",
        updates: {
          city: this.updateZipDataFieldDecorator("city")
        }
      }
  );

  handleSubmit = async (values: Partial<UpsertCompanyDto & { companyLogo: File }>, form: FormApi) => {
    const {companyId, onSave, getMandate} = this.props;

    let zip = "";
    let city = "";

    if (values.zip) {
      if (values.zip.includes(', ')) {
        zip = values.zip.split(", ")[0];
      } else {
        zip = values.zip;
      }
    }

    if (values.city) {
      if (values.city.includes(', ')) {
        city = values.city.split(", ")[1];
      } else {
        city = values.city;
      }
    }

    let logo;
    if (values.companyLogo) {
      logo = await toBase64(values.companyLogo);
    }

    const upsertCompanyDto = {
      ..._.omit(values, "companyLogo"),
      mandateId: getMandate(),
      zip,
      city,
      logo
    };

    if (companyId) {
      await editCompany(companyId, upsertCompanyDto, this.cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    } else {
      await saveCompany(upsertCompanyDto, this.cancelTokenSource)
          .then(response => {
            this.props.setSelectedCompanyId(response.id)
            onSave();
          })
          .catch((e: any) => this.handleError(e.response.data))
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [
      errorUtils.invalidInput,
      errorUtils.invalidEmail
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  //If company field value is SCHOOL default field values is OTHER, if not the field is cleared
  resetCompanySubType = createDecorator({
    field: 'companyType',
    updates: {
      companySubType: (fieldValue, allValues?: Partial<UpsertCompanyDto>) => {

        return fieldValue && fieldValue == CompanyType.SCHOOL
            ? allValues && allValues.companySubType !== undefined ? allValues.companySubType
                : CompanySubType.OTHER : undefined;
      }
    }
  });

  resetGuarantorCompany = createDecorator({
    field: 'guarantorCompany',
    updates: {
      parentGuarantorCompanyId: (fieldValue, allValues?: Partial<UpsertCompanyDto>) => {
        return fieldValue || !allValues ? undefined : allValues.parentGuarantorCompanyId;
      },
      guarantorSupportedCompanyTypes: (fieldValue, allValues?: Partial<UpsertCompanyDto>) => {
        return fieldValue && allValues ? allValues.guarantorSupportedCompanyTypes : [];
      }
    }
  });

  resetAcceptCDRemark = createDecorator({
    field: 'acceptCD',
    updates: {
      acceptCDRemark: (fieldValue, allValues?: Partial<UpsertCompanyDto>) => {
        return fieldValue && allValues ? allValues.acceptCDRemark : undefined;
      }
    }
  });

  render(): React.ReactNode {

    const {requiredDataLoaded} = this.state;

    if (!requiredDataLoaded) {
      return <></>;
    }

    return (
        <FinalForm
            onSubmit={(values, form) => this.handleSubmit(values, form)}
            validate={(values: Partial<UpsertCompanyDto & { companyLogo: File }>) => {
              let errors: { [key: string]: string } = {};
              if (values.companyType === CompanyType.SCHOOL && !values.companySubType) {
                errors.companySubType = "Required";
              }
              return errors;
            }}
            decorators={[
              this.zipDataDecorator,
              this.resetCompanySubType,
              this.resetGuarantorCompany,
              this.resetAcceptCDRemark
            ]}
            initialValues={this.initialCompanyValues}
            subscription={{pristine: true, submitting: true, values: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({form, handleSubmit, values, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    const stockManagementEnabled = this.props.currentUserHasPrivilege(Privilege.PX_CORE_STOCK_MANAGEMENT);

    return (
        <UpsertForm onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                {this.renderBasicInformationSection(form, values)}
                {values.guarantorCompany && this.renderGuarantorSupportedCompanyTypes()}
                {!values.guarantorCompany && this.renderCompanyOnboardingSection(form, values)}
                {!values.guarantorCompany && stockManagementEnabled && this.renderArticleGroupsSection(form, values)}
                {!values.guarantorCompany && this.renderCompanyCertificatesSection(form, values)}
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>
                {getMessage("button.save")}
              </Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}
              </Button>
            </FormButtonsRow>
          </FormContainer>
        </UpsertForm>
    );
  };

  renderGuarantorSupportedCompanyTypes = () => {

    const {getMessage} = this.props;
    const {companyTypes} = this.state;

    return (
        <>
          <Grid.Row>
            <Grid.Column>
              <Header as="h5" style={{marginTop: "1rem"}}>
                {getMessage("company.guarantorSupportedCompanyTypes")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16} verticalAlign="middle">
              <Field name="guarantorSupportedCompanyTypes"
                     component={Select}
                     options={companyTypes}
                     clearable
                     multiple
                     fluid
              />
            </Grid.Column>
          </Grid.Row>
        </>
    );
  }

  renderArticleGroupsSection = (form: FormApi, values: any): React.ReactNode => {
    const {getMessage} = this.props;
    const {
      articleGroupId,
      articleGroupIx,
      productId,
      testTypes,
      articleGroups,
      sampleMethod,
      logistics,
      generateCertificate,
      generateReport,
      sampleMethodDataSelected,
      articleGroupDataSelected,
      products,
      productsPerExternalCode,
      productDataSelected
    } = this.state;

    const companyArticleGroups = values.companyArticleGroups;
    const disabled = values.guarantorCompany;

    const usedArticleGroups: ArticleGroupDto[] = companyArticleGroups && companyArticleGroups.length > 0
        ? companyArticleGroups.map((cag: CompanyArticleGroupDto) => articleGroups.get(cag.articleGroupId))
        : [];

    const disabledSaveButton = sampleMethodDataSelected && articleGroupDataSelected;

    return (
        <React.Fragment>
          <Grid.Row className="sectionHeader">
            <Grid.Column>
              <Header as="h5">
                {getMessage("company.articleGroups")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <TableDiv>
                <Table celled selectable size="small">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{getMessage('testType')}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage('company.articleGroup')}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage('common.product')}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage('testSampleMethod')}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage('company.logistics')}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage('company.generateCertificate')}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage('company.generateReport')}</Table.HeaderCell>
                      <Table.HeaderCell textAlign={"center"}>{getMessage('actions')}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {!disabled && testTypes &&
                        testTypes.map((testType, index) => {

                          const articleGroup: ArticleGroupDto | undefined = usedArticleGroups
                              .find(ag => ag !== undefined && ag.externalCode === testType.value);

                          let sampleMethodForAg: string = "";
                          let logisticsForAg = false;
                          let generateCertificateForAg = false;
                          let generateReportForAg = false;
                          let companyArticleGroup = undefined;

                          if (articleGroup && articleGroup.id) {
                            companyArticleGroup = companyArticleGroups
                                .find((sm: CompanyArticleGroupDto) => sm.articleGroupId === articleGroup.id);
                            if (companyArticleGroup) {
                              sampleMethodForAg = companyArticleGroup.sampleMethod;
                              logisticsForAg = companyArticleGroup.logistics;
                              generateCertificateForAg = companyArticleGroup.generateCertificate
                              generateReportForAg = companyArticleGroup.generateReport
                            }
                          }

                          let articleGroupOptions: DropdownOption[] =
                              this.state.articleGroupsPerExternalCode.get(testType.value) || [];

                          let sampleMethodOptions: DropdownOption[] =
                              this.state.testSampleMethods;

                          const product: ProductDto | undefined =
                              companyArticleGroup && companyArticleGroup.productId
                                  ? products.get(companyArticleGroup.productId)
                                  : undefined;

                          const productOptions: DropdownOption[] =
                              articleGroup ? productsPerExternalCode.get(articleGroup.externalCode) || [] : [];

                          const editing = articleGroupIx === index;
                          return (
                              <Table.Row key={index}>
                                <Table.Cell width={4}>
                                  {testType.text}
                                </Table.Cell>
                                <Table.Cell width={5}>
                                  {
                                    editing
                                        ? <PxDropdownSc
                                            value={articleGroupId}
                                            onChange={
                                              (e: any, {value}: DropdownProps) =>
                                                  this.onArticleGroupChange(value)
                                            }
                                            options={articleGroupOptions}
                                            selection
                                            search
                                            fluid
                                            className={`dropdown ${!articleGroupDataSelected ? 'error-mandatory-field' : ''}`}
                                        />
                                        :
                                        articleGroup
                                            ? [
                                              articleGroup.description,
                                              articleGroup.externalOrderNumber,
                                              articleGroup.testLaboratoryDescription
                                            ].filter(Boolean).join(", ")
                                            : ""
                                  }
                                </Table.Cell>
                                <Table.Cell width={5}>
                                  {
                                    editing
                                        ? <PxDropdownSc
                                            value={productId}
                                            onChange={
                                              (e: any, {value}: DropdownProps) =>
                                                  this.onProductGroupChange(value)
                                            }
                                            options={productOptions}
                                            selection
                                            search
                                            fluid
                                            clearable
                                        />
                                        :
                                        product
                                            ? [
                                              product.id,
                                              product.description
                                            ].filter(Boolean).join(" - ")
                                            : ""
                                  }
                                </Table.Cell>
                                <Table.Cell width={3} textAlign={"left"}>
                                  {
                                    editing
                                        ? <PxDropdownSc
                                            value={sampleMethod}
                                            onChange={
                                              (e: any, {value}: DropdownProps) =>
                                                  this.onSampleMethodChange(value)
                                            }
                                            options={sampleMethodOptions}
                                            selection
                                            fluid
                                            className={`dropdown ${!sampleMethodDataSelected ? 'error-mandatory-field' : ''}`}
                                        />
                                        : articleGroup && sampleMethodForAg
                                            ? getMessage("covidTestSampleMethod." + sampleMethodForAg)
                                            : ""
                                  }
                                </Table.Cell>
                                <Table.Cell width={1} textAlign={"center"}>
                                  {
                                    editing
                                        ? <CheckboxSc
                                            checked={logistics}
                                            onChange={() => this.logisticsClicked()}
                                        />
                                        : articleGroup && logisticsForAg
                                            ? <Icon name="checkmark"></Icon>
                                            : <></>
                                  }
                                </Table.Cell>
                                <Table.Cell width={1} textAlign={"center"}>
                                  {
                                    editing
                                        ? <CheckboxSc
                                            checked={generateCertificate}
                                            onChange={() => this.generateCertificateClicked()}
                                        />
                                        : articleGroup && generateCertificateForAg
                                            ? <Icon name="checkmark"></Icon>
                                            : <></>
                                  }
                                </Table.Cell>
                                <Table.Cell width={1} textAlign={"center"}>
                                  {
                                    editing
                                        ? <CheckboxSc
                                            checked={generateReport}
                                            onChange={() => this.generateReportClicked()}
                                        />
                                        : articleGroup && generateReportForAg
                                            ? <Icon name="checkmark"></Icon>
                                            : <></>
                                  }
                                </Table.Cell>
                                <Table.Cell width={2} textAlign={"center"}>
                                  {editing
                                      ? <React.Fragment>
                                        <SaveActionButton hoverMessage={getMessage('button.save')}
                                                          onConfirm={() =>
                                                              this.saveCompanyArticleGroup(values, form, articleGroup)}
                                                          disabled={!disabledSaveButton}
                                        />
                                        <CancelActionButton hoverMessage={getMessage('button.cancel')}
                                                            onConfirm={() => this.cancelEditCompanyArticleGroup()}/>
                                      </React.Fragment>
                                      : <React.Fragment>
                                        <EditActionButton
                                            hoverMessage={getMessage('msgEdit')}
                                            onConfirm={() => this.editCompanyArticleGroup(values, articleGroup, index)}
                                        />
                                        <DeleteActionButton
                                            hoverMessage={getMessage("msgDelete")}
                                            onConfirm={() => this.clearCompanyArticleGroup(values, form, articleGroup)}
                                        />
                                      </React.Fragment>
                                  }
                                </Table.Cell>
                              </Table.Row>
                          );
                        })
                    }
                  </Table.Body>
                </Table>
              </TableDiv>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

  renderCompanyCertificatesSection = (form: FormApi, values: any): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column>
              <Header as="h5" style={{marginTop: "1rem"}}>
                {getMessage("company.externalCertificates")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5}>
              <Field
                  name="recoveryCertificate"
                  component={CheckBox}
                  label={`${getMessage("company.recoveryCertificate")}`}
              />
            </Grid.Column>
            <Grid.Column width={5}>
              <Field
                  name="vaccinationCertificate"
                  component={CheckBox}
                  label={`${getMessage("company.vaccinationCertificate")}`}
              />
            </Grid.Column>
            <Grid.Column width={5}>
              <Field
                  name="testCertificate"
                  component={CheckBox}
                  label={`${getMessage("company.testCertificate")}`}
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  }
  onSampleMethodChange = (sampleMethod: any) => {
    this.setState({
      sampleMethod: sampleMethod
    });

    if (sampleMethod === undefined || sampleMethod === "") {
      this.setState({
        sampleMethodDataSelected: false
      })
    } else {
      this.setState({
        sampleMethodDataSelected: true
      })
    }
  };

  logisticsClicked = () => {
    this.setState(
        (oldState) => {
          const newLogisticsState = !oldState.logistics;
          return {logistics: newLogisticsState}
        });
  }

  generateCertificateClicked = () => {
    this.setState(
        (oldState) => {
          const newGenerateCertificateState = !oldState.generateCertificate;
          return {generateCertificate: newGenerateCertificateState}
        });
  }

  generateReportClicked = () => {
    this.setState(
        (oldState) => {
          const newGenerateReportState = !oldState.generateReport;
          return {generateReport: newGenerateReportState}
        });
  }

  onArticleGroupChange = (articleGroupId: any) => {
    this.setState({
      articleGroupId: typeof articleGroupId === "number" && articleGroupId >= 0 ? articleGroupId : undefined
    });

    if (articleGroupId === undefined || articleGroupId === "" || articleGroupId <= 0) {
      this.setState({
        articleGroupDataSelected: false
      })
    } else {
      this.setState({
        articleGroupDataSelected: true
      })
    }
  };

  onProductGroupChange = (productId: any) => {
    this.setState({
      productId: typeof productId === "string" && productId ? productId : undefined
    });

    this.setState({
      productDataSelected: !!productId
    });
  };

  editCompanyArticleGroup = (
      values: any, articleGroup: ArticleGroupDto | undefined, ix: number) => {

    let companyArticleGroups = values.companyArticleGroups ? [...values.companyArticleGroups] : [];

    let index = -1;
    if (articleGroup) {
      index = companyArticleGroups.findIndex(cag => cag.articleGroupId === articleGroup.id);
    }

    let articleGroupId = undefined;
    let productId: string | undefined = undefined;
    let sampleMethod = undefined;
    let logistics = false;
    let generateCertificate = false;
    let generateReport = false;

    if (companyArticleGroups) {
      const companyArticleGroup: CompanyArticleGroupDto = companyArticleGroups[index];
      if (companyArticleGroup) {
        articleGroupId = companyArticleGroup.articleGroupId;
        productId = companyArticleGroup.productId;
        sampleMethod = companyArticleGroup.sampleMethod;
        logistics = companyArticleGroup.logistics;
        generateCertificate = companyArticleGroup.generateCertificate;
        generateReport = companyArticleGroup.generateReport;
      }
    }

    if (articleGroupId !== undefined && articleGroupId > 0) {
      this.setState({
        articleGroupDataSelected: true
      })
    }

    if (productId) {
      this.setState({
        productDataSelected: true
      })
    }

    if (sampleMethod !== undefined && sampleMethod !== null) {
      this.setState({
        sampleMethodDataSelected: true
      })
    }

    this.setState({
      articleGroupId: articleGroupId,
      articleGroupIx: ix,
      productId: productId,
      sampleMethod: sampleMethod,
      logistics: logistics,
      generateCertificate: generateCertificate,
      generateReport: generateReport
    });
  };

  clearCompanyArticleGroup = (values: any, form: FormApi, articleGroup: ArticleGroupDto | undefined) => {

    let companyArticleGroups = values.companyArticleGroups ? [...values.companyArticleGroups] : [];

    let index = -1;
    if (articleGroup) {
      index = companyArticleGroups.findIndex(cag => cag.articleGroupId === articleGroup.id);
    }

    if (companyArticleGroups.length > 0) {
      companyArticleGroups = companyArticleGroups.filter((cag, ix) => index !== ix);
      form.change("companyArticleGroups", companyArticleGroups);
    }
  };

  saveCompanyArticleGroup = (
      values: any, form: FormApi, articleGroup: ArticleGroupDto | undefined) => {

    const {
      articleGroupId,
      productId,
      sampleMethod,
      logistics,
      generateCertificate,
      generateReport
    } = this.state;

    let companyArticleGroups = values.companyArticleGroups ? [...values.companyArticleGroups] : [];

    let index = -1;
    if (articleGroup) {
      index = companyArticleGroups.findIndex(cag => cag.articleGroupId === articleGroup.id);
    }

    if (index >= 0) {
      if (articleGroupId !== undefined && sampleMethod !== undefined) {
        companyArticleGroups[index] = {
          ...companyArticleGroups[index],
          articleGroupId: articleGroupId,
          productId: productId,
          sampleMethod: sampleMethod,
          logistics: logistics,
          generateCertificate: generateCertificate,
          generateReport: generateReport
        };
      } else {
        companyArticleGroups = companyArticleGroups.filter((cag, ix) => index !== ix);
      }
    } else {
      if (articleGroupId !== undefined && sampleMethod !== undefined) {
        const newCompanyArticleGroup = {
          articleGroupId: articleGroupId,
          productId: productId,
          sampleMethod: sampleMethod,
          logistics: logistics,
          generateCertificate: generateCertificate,
          generateReport: generateReport
        };
        companyArticleGroups.push(newCompanyArticleGroup);
      }
    }

    form.change("companyArticleGroups", companyArticleGroups);

    this.setState({
      articleGroupIx: undefined,
      articleGroupId: undefined,
      productId: undefined,
      sampleMethod: undefined,
      logistics: false,
      generateCertificate: false,
      generateReport: false,
      sampleMethodDataSelected: false,
      articleGroupDataSelected: false,
      productDataSelected: false
    });
  };

  cancelEditCompanyArticleGroup = () => {
    this.setState({
      articleGroupIx: undefined,
      articleGroupId: undefined,
      productId: undefined,
      sampleMethod: undefined,
      logistics: false,
      generateCertificate: false,
      generateReport: false,
      sampleMethodDataSelected: false,
      articleGroupDataSelected: false,
      productDataSelected: false
    });
  };

  renderCompanyOnboardingSection = (form: FormApi, values: Partial<UpsertCompanyDto>): React.ReactNode => {

    const {
      testRegistrationTypes,
      testTypes,
      testSampleMethods,
      testPoolingTypes,
      repetitiveTestingModes
    } = this.state;

    const {companyId, company, getMessage} = this.props;

    const disabled = values.guarantorCompany;
    const disableAcceptCD = companyId && company
        && (company.acceptCD || company.registrationState == RegistrationStateType.ACCEPTED);
    const disableAcceptCDRemark = !values.acceptCD;

    const selectMap: IndexSignature = {
      testRegistrationType: testRegistrationTypes,
      testType: testTypes,
      testSampleMethod: testSampleMethods,
      testPoolingType: testPoolingTypes,
      repetitiveTestingMode: repetitiveTestingModes
    };

    const leftInfoFields = [

      selectInput({
            name: "testRegistrationType",
            clearable: true,
            disabled: disabled
          },
          selectMap
      ),
      selectInput({
            name: "testPoolingType",
            clearable: true,
            disabled: disabled
          },
          selectMap
      )
    ];

    const rightInfoFields = [
      textInput({
        name: "acceptCDRemark",
        disabled: (disableAcceptCD || disableAcceptCDRemark)
      }),
      selectInput({
            name: "testType",
            clearable: true,
            disabled: disabled
          },
          selectMap
      ),
      selectInput({
            name: "testSampleMethod",
            clearable: true,
            disabled: disabled
          },
          selectMap
      )
    ];

    let mappedLeftRow = leftInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage(fieldProps.name)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {..._.omit(fieldProps, "hiddenInput")}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    const automaticCertificateNotification = (
        <Grid.Row key={-1}>
          <Grid.Column width={5} verticalAlign="middle">
          </Grid.Column>
          <Grid.Column width={11} verticalAlign="middle">
            <Field
                name="automaticCertificateNotification"
                component={CheckBox}
                label={`${getMessage("company.automaticCertificateNotification")}`}
            />
          </Grid.Column>
        </Grid.Row>
    );
    mappedLeftRow.splice(1, 0, automaticCertificateNotification);

    const acceptCDCheckbox = (
        <Grid.Row key={-2}>
          <Grid.Column width={5} verticalAlign="middle">
          </Grid.Column>
          <Grid.Column width={11} verticalAlign="middle">
            <Field
                name="acceptCD"
                component={CheckBox}
                label={`${getMessage("company.acceptContractDocuments")}`}
                disabled={disableAcceptCD}
            />
          </Grid.Column>
        </Grid.Row>
    );
    mappedLeftRow.unshift(acceptCDCheckbox);

    const mappedRightRow = rightInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage(fieldProps.name)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {..._.omit(fieldProps, "hiddenInput")}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    const repetitiveTestingSelect = selectInput({
          id: "repetitiveTestingMode",
          name: "repetitiveTestingMode",
          validate: required
        },
        selectMap
    );

    var showRepetitiveTestingSelect = !values.guarantorCompany && values.parentGuarantorCompanyId;

    return (

        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedLeftRow}
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedRightRow}
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>

          {
              showRepetitiveTestingSelect &&
              <>
                <Grid.Row className="sectionHeader">
                  <Grid.Column width={8} verticalAlign="middle" textAlign="left">
                    <Header as="h5">{getMessage("company.repetitiveTestingMode")}</Header>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={10} verticalAlign="middle">
                    <Field
                        fluid
                        {..._.omit(repetitiveTestingSelect, "hiddenInput")}
                    />
                  </Grid.Column>
                </Grid.Row>
              </>
          }
        </React.Fragment>
    );
  };


  renderBasicInformationSection = (form: FormApi, values: Partial<UpsertCompanyDto & { companyLogo: File }>): React.ReactNode => {

    const {
      countries,
      billTexts,
      companyTypes,
      companySubTypes,
      cities,
      zip,
      guarantorCompanies,
      labInvoiceTo,
      containerOptions
    } = this.state;

    const {getMessage, companyId} = this.props;

    const validator = (value: string) => required(value);

    const guarantorCompanyEditability = companyId != undefined && companyId > 0;

    const selectMap: IndexSignature = {
      countryId: countries,
      parentGuarantorCompanyId: guarantorCompanies,
      labInvoiceTo: labInvoiceTo,
      language: billTexts,
      companyType: companyTypes,
      companySubType: companySubTypes,
      guarantorCompanyAllowedCompanyType: companyTypes,
      city: cities,
      zip: zip,
      container: containerOptions
    };

    const countryId = form.getState().values.countryId;
    let disabledSubCompanyType = true;
    if (values.companyType && values.companyType === CompanyType.SCHOOL) {
      disabledSubCompanyType = false;
    }

    const disabled = values.guarantorCompany;

    let basicLeftInfoFields = [
      textInput({
            name: "description",
            validate: validator
          }
      ),
      textInput({
            name: "street"
          }
      ),
      selectInput({
            name: "zip",
            validate: validator,
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: ((searchQuery: string) => this.searchZipData(searchQuery, countryId)),
            onNewAddition: (this.onNewAddition("zip"))
          },
          selectMap
      ),
      selectInput({
            name: "city",
            validate: validator,
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: ((searchQuery: string) => this.searchZipData(searchQuery, countryId)),
            onNewAddition: (this.onNewAddition("cities"))
          },
          selectMap
      ),
      selectInput({
            name: "countryId",
            validate: validator
          },
          selectMap
      ),
      selectInput({
            name: "container",
            clearable: true
          },
          selectMap
      )
    ];

    if (!values.guarantorCompany) {
      basicLeftInfoFields.push(
          selectInput({
                name: "parentGuarantorCompanyId",
                clearable: true,
                disabled: disabled
              },
              selectMap
          )
      )
    }

    if (values.guarantorCompany) {
      basicLeftInfoFields.push(
          selectInput({
                name: "labInvoiceTo",
                clearable: true,
                validate: required
              },
              selectMap
          )
      )
    }

    const basicRightInfoFields = [
      textInput({
        name: "tel"
      }),
      textInput({
        name: "email"
      }),
      textInput({
        name: "homepage"
      }),
      selectInput({
            name: "language",
            validate: validator
          },
          selectMap
      ),
      selectInput({
            name: "companyType",
            validate: validator,
            clearable: true
          },
          selectMap
      ),
      selectInput({
            name: "companySubType",
            clearable: true,
            disabled: disabledSubCompanyType
          },
          selectMap
      ),
      dropzoneInput(
          {
            name: "companyLogo",
            message: getMessage("company.upload.logo"),
            isImage: true,
            imageContentBase64: values["logoContentBase64"],
            imageFileType: values["logoFileType"]
          }
      )

    ];

    const mappedLeftRow = basicLeftInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage("company." + fieldProps.name)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {..._.omit(fieldProps, "hiddenInput")}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    const mappedRightRow = basicRightInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage("company." + fieldProps.name)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {..._.omit(fieldProps, "hiddenInput")}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    const selfOnboardedInfo = values.selfOnboarding ? " : " + getMessage("company.selfOnboarded") : "";
    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={8} verticalAlign="middle" textAlign="left">
              <Header as="h5">{getMessage("company.basicInformation")} {`${selfOnboardedInfo}`}</Header>
            </Grid.Column>
            <Grid.Column width={4} textAlign="right">
              <Field
                  name="guarantorCompany"
                  component={CheckBox}
                  label={getMessage("company.guarantorCompany")}
                  toggle
                  disabled={guarantorCompanyEditability}
              />
            </Grid.Column>
            <Grid.Column width={4} textAlign="right">
              <Field
                  name="active"
                  component={CheckBox}
                  label={getMessage("company.active")}
                  toggle
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedLeftRow}
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedRightRow}
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

}

export default withAuthContext(withMessages(withGuarantorsContext(UpsertCompanyForm)));
